/**
 * @desc        Animal IDs formatting utilities
 * @version     0.0.1
 */

import { ANIMAL_OPTIONS } from 'helper-functions/livestock/config/animal-config.js'
import { speciesAgeClassRanges } from 'helper-functions/livestock/config/species-ageclasses.js'
import { translateSpeciesUiLabel, translateAgeClassLabel } from 'features-v2/animal-management/helpers/species-display-i18n.js'
import { format } from 'date-fns'

/**
 * Converts ranges or object format of animal IDs to an array of IDs
 * @param {Array|Object} input - Array of ranges (e.g., [[239, 249], [251, 260]]), single numbers (e.g., [456]), or Object with animal IDs as keys
 * @returns {Array} Array of animal IDs
 */
export const reverseFormatAnimalIds = input => {
    if (!input) return []

    // Handle array: can be ranges [[239, 249], [251, 260]], single numbers [456], or mixed
    if (Array.isArray(input)) {
        // Handle single number in array: [456]
        if (input.length === 1 && typeof input[0] === 'number') {
            return [input[0]]
        }

        const result = []
        input.forEach(item => {
            if (Array.isArray(item)) {
                // Range: [start, end]
                if (item.length === 2) {
                    const [start, end] = item
                    for (let i = start; i <= end; i++) {
                        result.push(i)
                    }
                }
                // Single number in nested array: [[456]] - treat as single number
                else if (item.length === 1) {
                    const num = typeof item[0] === 'string' ? Number(item[0]) : item[0]
                    if (!isNaN(num)) {
                        result.push(num)
                    }
                }
            } else {
                // Single number or string number: [456] or ["456"]
                const num = typeof item === 'string' ? Number(item) : item
                if (typeof num === 'number' && !isNaN(num)) {
                    result.push(num)
                }
            }
        })
        return result
    }

    // Handle single number (not in array): 456
    if (typeof input === 'number') {
        return [input]
    }

    // Handle object format: {1: true, 2: true} (backward compatibility)
    if (typeof input === 'object') {
        return Object.keys(input)
            .filter(key => input[key] != null && input[key] !== false)
            .map(key => Number(key))
            .filter(id => !isNaN(id))
    }

    return []
}

/**
 * Converts an array of animal objects or IDs to the range format expected by the API
 * @param {Array} animals - Array of animal objects with id property (e.g., [{id: 1}, {id: 2}]) or array of IDs (e.g., [1, 2, 3])
 * @returns {Array} Array with ranges and single numbers (e.g., [[1,3],[99,101],2004,2009,[78,81]])
 */
export const formatAnimalIds = animals => {
    if (!Array.isArray(animals) || animals.length === 0) return []

    // Extract IDs from animal objects or use numbers directly
    const ids = animals
        .map(animal => {
            if (typeof animal === 'number') return animal
            if (typeof animal === 'string') return Number(animal)
            return animal?.id != null ? Number(animal.id) : null
        })
        .filter(id => id != null && !isNaN(id))
        .sort((a, b) => a - b)

    if (ids.length === 0) return []

    const result = []
    let rangeStart = ids[0]
    let rangeEnd = ids[0]

    for (let i = 1; i < ids.length; i++) {
        if (ids[i] === rangeEnd + 1) {
            // Consecutive number, extend the range
            rangeEnd = ids[i]
        } else {
            // Gap found, save current range/single number and start new one
            if (rangeStart === rangeEnd) {
                // Single number
                result.push(rangeStart)
            } else {
                // Range
                result.push([rangeStart, rangeEnd])
            }
            rangeStart = ids[i]
            rangeEnd = ids[i]
        }
    }

    // Add the last range or single number
    if (rangeStart === rangeEnd) {
        result.push(rangeStart)
    } else {
        result.push([rangeStart, rangeEnd])
    }

    return result
}

/**
 * True when `ids` lists an animal key matching `needle` (string vs number coercion). Never throws.
 * @param {Array|undefined|null} ids
 * @param {string|number|null|undefined} needle
 */
export const idsListIncludesAnimalId = (ids, needle) => {
    if (!Array.isArray(ids)) return false
    if (needle === undefined || needle === null || needle === '') return false

    const nNeedlePrim = typeof needle === 'number' ? needle : Number(needle)
    const needleIsFiniteNum = Number.isFinite(nNeedlePrim)
    const needleStr = String(needle)

    for (const slot of ids) {
        if (slot === needle) return true
        if (slot != null && String(slot) === needleStr) return true
        if (needleIsFiniteNum && Number.isFinite(Number(slot)) && Number(slot) === nNeedlePrim) return true
    }
    return false
}

/**
 * Extracts all individual animals from a group/mob object or cohort array
 * @param {Object|Array} input - Group object with animals array, or cohort array directly
 * @returns {Array} Array of individual animal objects with id, farmId, detail, paddockId
 */
export const extractIndividualAnimals = input => {
    // Handle cohort array directly
    if (Array.isArray(input)) {
        return input.flatMap(cohort => {
            const ids = reverseFormatAnimalIds(cohort?.ids)
            return ids.map(id => ({
                id,
                farmId: cohort?.farmId ?? null,
                detail: cohort?.detail ? { ...cohort.detail } : {},
                paddockId: cohort?.paddockId ?? null,
            }))
        })
    }

    // Handle group object with animals array
    if (!input?.animals || !Array.isArray(input.animals)) return []

    return input.animals.flatMap(animal => {
        const ids = reverseFormatAnimalIds(animal?.ids)
        return ids.map(id => ({
            id,
            farmId: animal?.farmId ?? null,
            detail: animal?.detail ? { ...animal.detail } : {},
            paddockId: animal?.paddockId ?? null,
        }))
    })
}

/**
 * Creates a deterministic 8-character uppercase hex identifier for a cohort
 * using an FNV-1a 32-bit hash over the provided IDs (order-sensitive).
 *
 * @param {Array<string|number>} ids - List of animal or cohort IDs to hash.
 * @returns {string} Deterministic 8-character uppercase hexadecimal hash.
 */
export const cohortIdHex = ids => {
    let h = 0x811c9dc5 // FNV-1a 32-bit offset basis
    const sorted = [...ids]
        .map(Number)
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b)

    for (const id of sorted) {
        const s = `${id},` // delimiter avoids ambiguity
        for (let i = 0; i < s.length; i++) {
            h ^= s.charCodeAt(i)
            h = Math.imul(h, 0x01000193) >>> 0 // FNV prime
        }
    }

    return h.toString(16).toUpperCase().padStart(8, '0') // e.g. "3FA91C2E"
}

export const cohortIdHex2 = ids => {
    if (!Array.isArray(ids) || ids.length === 0) return '0000000000000000'
    const sorted = [...ids]
        .map(Number)
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b)
    let lo = 0
    let hi = 0
    for (const id of sorted) {
        lo = (Math.imul(lo, 31) + id) >>> 0
        hi = (Math.imul(hi, 37) + id) >>> 0
    }
    const loHex = (lo >>> 0).toString(16).toUpperCase().padStart(8, '0')
    const hiHex = (hi >>> 0).toString(16).toUpperCase().padStart(8, '0')
    return hiHex + loHex
}

export const singularSpeciesName = s => (s?.endsWith('s') ? s.slice(0, -1) : (s ?? ''))
export const toSpeciesKey = name => {
    const low = (name ?? '').toLowerCase()
    return speciesAgeClassRanges[low] ? low : low.endsWith('s') && speciesAgeClassRanges[low.slice(0, -1)] ? low.slice(0, -1) : low
}
export const titleCase = s => (s ?? '').replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())

export const createMenuItems = (uniqueSpecies = [], t) => {
    const tr = typeof t === 'function' ? t : k => k
    const set = new Set(uniqueSpecies)
    const matches = opt => set.has(opt) || uniqueSpecies.some(s => s.includes(singularSpeciesName((opt ?? '').toLowerCase())))
    const options = (ANIMAL_OPTIONS ?? []).filter(g => !g?.disabled && (g?.options ?? []).some(matches))
    const groups = options.map(group => ({
        type: 'group',
        id: (group.label ?? '').toLowerCase().replace(/\s+/g, '-'),
        label: translateSpeciesUiLabel(group.label, tr),
        items: (group.options ?? []).filter(matches).map(speciesName => {
            const key = toSpeciesKey(speciesName)
            const ranges = speciesAgeClassRanges[key] ?? {}
            const ageClasses = Object.keys(ranges).filter(ac => set.has(`${key}|${ac}`))
            const children = [{ id: `${key}|any`, label: tr('4676') }, ...ageClasses.map(ac => ({ id: `${key}|${ac}`, label: translateAgeClassLabel(ac, tr) }))]
            return { id: speciesName, label: translateSpeciesUiLabel(speciesName, tr), children }
        }),
    }))
    return [{ id: 'all-species', label: tr('4492') }, ...groups]
}

/**
 * Summarises the animals of a group into unique lists and group-level fields.
 * @param {object} group - Group with id, name, size, created, updated, animals[]
 * @returns {object} Summary with farmIds, species, ageClasses, genders, dateOfBirths, breeds, healthStatuses, paddockIds (arrays), plus size, id, name, created, updated
 */
export function summariseGroupAnimals(group) {
    const base = () => ({
        farmIds: [],
        species: [],
        ageClasses: [],
        genders: [],
        dateOfBirths: [],
        breeds: [],
        healthStatuses: [],
        paddockIds: [],
        size: group?.size,
        id: group?.id,
        name: group?.name,
        created: group?.created,
        updated: group?.updated,
    })
    if (!group || !Array.isArray(group.animals)) return base()

    const sets = {
        farmIds: new Set(),
        species: new Set(),
        ageClasses: new Set(),
        genders: new Set(),
        dateOfBirths: new Set(),
        breeds: new Set(),
        healthStatuses: new Set(),
        paddockIds: new Set(),
    }
    const add = (key, v) => {
        if (v != null) sets[key].add(v)
    }

    for (const a of group.animals) {
        try {
            const d = a?.detail
            const b = d?.birth
            add('farmIds', a?.farmId)
            add('paddockIds', a?.paddockId)
            add('species', b?.species ?? d?.species)
            add('ageClasses', b?.ageClass)
            add('genders', b?.gender)
            add('dateOfBirths', b?.dateOfBirth)
            add('breeds', d?.breed ?? b?.breed)
            add('healthStatuses', d?.health?.status)
        } catch {
            // skip malformed animal
        }
    }

    return {
        ...Object.fromEntries(Object.entries(sets).map(([k, set]) => [k, [...set]])),
        size: group.size,
        id: group.id,
        name: group.name,
        created: group.created,
        updated: group.updated,
    }
}

export const cohortSummary = cohorts => {
    if (!Array.isArray(cohorts) || cohorts.length === 0) {
        return []
    }

    // Helper function for date formatting with error handling
    const dobMonthKey = date => {
        if (date == null) return null
        try {
            // Ensure date is a valid Date object
            const dateObj = date instanceof Date ? date : new Date(date)
            if (isNaN(dateObj.getTime())) return null
            return format(dateObj, 'yyyy-MM')
        } catch (error) {
            console.warn('Error formatting date:', error, date)
            return null
        }
    }

    // Helper to get normalized species name
    // const getSpecies = cohort => {
    //     const species = cohort?.detail?.birth?.species ?? cohort?.detail?.species
    //     return species ? singularSpeciesName(species.toLowerCase()) : null
    // }

    // Filter cohorts by species if needed
    // let filteredCohorts = cohorts
    // const speciesFilter = selectedByMenuId?.species?.id
    // if (speciesFilter && speciesFilter !== 'all-species') {
    //     const selectedSpeciesKey = speciesFilter.split('|')[0]
    //     const selectedSpecies = singularSpeciesName(selectedSpeciesKey.toLowerCase())
    //     filteredCohorts = filteredCohorts.filter(cohort => {
    //         const cohortSpecies = getSpecies(cohort)
    //         return cohortSpecies && cohortSpecies === selectedSpecies
    //     })
    // }

    // Build species-to-animal-ids mapping in a single pass using reduce
    const speciesToAnimalIds = cohorts.reduce((acc, cohort) => {
        const species = cohort?.detail?.birth?.species ?? cohort?.detail?.species
        if (!species) return acc

        // Get animal IDs with validation
        const ids = reverseFormatAnimalIds(cohort?.ids)
        if (!Array.isArray(ids) || ids.length === 0) return acc

        // Initialize species entry if needed
        if (!acc[species]) {
            acc[species] = {
                bySpecies: [],
                byPaddock: {},
                byAgeClass: {},
                byGender: {},
                byBreed: {},
                byDateOfBirth: {},
            }
        }

        const sp = acc[species]
        sp.bySpecies.push(...ids)

        // Helper to add IDs to a grouping map
        const addToMap = (map, key) => {
            if (key != null) {
                if (!map[key]) {
                    map[key] = []
                }
                map[key].push(...ids)
            }
        }

        // Extract and add attributes
        addToMap(sp.byPaddock, cohort?.paddockId ?? null)
        addToMap(sp.byAgeClass, cohort?.detail?.birth?.ageClass ?? null)
        addToMap(sp.byGender, cohort?.detail?.birth?.gender ?? null)
        addToMap(sp.byBreed, cohort?.detail?.breed ?? cohort?.detail?.birth?.breed ?? null)
        addToMap(sp.byDateOfBirth, dobMonthKey(cohort?.detail?.birth?.dateOfBirth))

        return acc
    }, {})

    // Build result list from species mappings
    const speciesToAnimalIdsList = Object.entries(speciesToAnimalIds).flatMap(([species, data]) => {
        // Build items array starting with bySpecies
        const items = [{ species, group: 'bySpecies', key: null, animalIds: data.bySpecies }]

        // Add all grouped items
        const byMaps = [
            ['byPaddock', data.byPaddock],
            ['byAgeClass', data.byAgeClass],
            ['byGender', data.byGender],
            ['byBreed', data.byBreed],
            ['byDateOfBirth', data.byDateOfBirth],
        ]

        byMaps.forEach(([group, map]) => {
            Object.entries(map).forEach(([key, animalIds]) => {
                if (Array.isArray(animalIds) && animalIds.length > 0) {
                    items.push({ species, group, key, animalIds, size: animalIds.length })
                }
            })
        })

        // Check if all items (except bySpecies) have equal size
        const [first, ...rest] = items
        if (rest.length === 0) return [first]

        const sizes = rest.map(item => item.size)
        const uniqueSizes = new Set(sizes)

        // If all sizes are equal, consolidate into first item
        if (uniqueSizes.size === 1) {
            first.data = rest.map(({ group, key }) => ({ group, key }))
            return [first]
        }

        return items
    })

    return speciesToAnimalIdsList
}
